/**
 * @description Layout principal para modo interno com drawer e header
 * @module InternalLayout
 * @since Fevereiro 2024
 */

import React, { useState } from "react";

import {
  Box,
  Drawer,
  Toolbar,
  Divider,
  useTheme,
  CircularProgress,
  useMediaQuery,
  Dialog,
} from "@mui/material";
import {} from "@mui/icons-material";

import { useSessionContext } from "@/contexts/SessionContext";

import { useSidebarNavigation } from "@/contexts/SidebarNavigationContext";

import { ViewSwitcher } from "./components/Drawer/ViewSwitcher";

import { currentFeatures } from "@/configs/Settings";

import { MobileBarNavigation } from "./components/MobileBarNavigation";
import { MainContent } from "./components/MainContent";

import { VersionViewer } from "./components/Drawer/VersionViewer";

import { LayoutPrivateHeaderContent } from "./components/Header/LayoutPrivateHeader";
import { SideBarMainContent } from "./components/Drawer/SideBarMainContent";
import { ChatLuxor } from "@/components/common/ChatLuxor";

export const drawerWidth = 280;

interface PrivateMainLayoutProps {
  children: React.ReactNode;
  noSidebar?: boolean;
  dashboard?: boolean;
  noPadding?: boolean;
}

const PrivateMainLayout: React.FC<PrivateMainLayoutProps> = ({
  children,
  noSidebar,
  dashboard,
  noPadding,
}) => {
  const { roles, loading, access } = useSessionContext();
  const { isSidebarExpanded: open, setIsSidebarExpanded: setOpen } =
    useSidebarNavigation();

  const themes = useTheme();

  const isMediumScreen = useMediaQuery(themes.breakpoints.up("md"));

  const isDesktopScreen = useMediaQuery(themes.breakpoints.up("lg"));

  const handleDrawerToggle = () => {
    setOpen(!open);
  };

  const [avatarMenuAnchorEl, setAvatarMenuAnchorEl] =
    useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAvatarMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAvatarMenuAnchorEl(null);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const content = (
    <>
      <LayoutPrivateHeaderContent
        isMediumScreen={isMediumScreen}
        handleDrawerToggle={handleDrawerToggle}
        avatarMenuAnchorEl={avatarMenuAnchorEl}
        handleMenuOpen={handleMenuOpen}
        handleMenuClose={handleMenuClose}
      />

      {isMediumScreen && !noSidebar && !currentFeatures.register && (
        <Drawer
          onClose={handleDrawerToggle}
          sx={{
            width: drawerWidth,
            flexShrink: 0,

            "& .MuiDrawer-paper": {
              width: drawerWidth,
              boxSizing: "border-box",
            },
          }}
          variant={isDesktopScreen ? "persistent" : "temporary"}
          anchor="left"
          open={open}
          ModalProps={!isDesktopScreen ? { keepMounted: true } : undefined}
        >
          <Toolbar />
          <Divider />
          <SideBarMainContent isMediumScreen={isMediumScreen} />

          {roles !== undefined &&
            roles?.length > 0 &&
            !currentFeatures.register && <ViewSwitcher isDrawerOpen={open} />}

          <VersionViewer />
        </Drawer>
      )}

      <Box component="main" sx={{ flexGrow: 1, p: 0.01 }}>
        <Toolbar />
        <MainContent
          open={open}
          sx={{ p: noPadding ? 0 : 2 }}
          isMediumScreen={isMediumScreen}
          shouldShiftForDrawer={isDesktopScreen}
        >
          {children}
          {!isMediumScreen && <MobileBarNavigation />}

          {currentFeatures.defaultApp && <ChatLuxor />}
        </MainContent>
      </Box>
    </>
  );

  if (!isMediumScreen) {
    return (
      <Dialog
        fullScreen
        open={true}
        sx={{
          "& .MuiDialog-paper": {
            bgcolor: "#F4F6F8",
          },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
          {content}
        </Box>
      </Dialog>
    );
  }

  return <Box sx={{ display: "flex" }}>{content}</Box>;
};
export { PrivateMainLayout };
